import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import map from "../../data/cgDistricts.json";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ShopImport } from "./ShopImport";
import { SHOP_SERVICES } from "../../data/services";

const EMPTY = { name: "", district: "", address: "", pincode: "", phone: "", services: [], visible: true };
const inputCls = "mt-1 h-10 w-full rounded-lg border border-white/10 bg-ink px-3 text-sm outline-none focus:border-saffron";

export const ShopsPanel = () => {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/admin/shops").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const save = async (item) => {
    try {
      const body = { name: item.name, district: item.district, address: item.address, pincode: item.pincode, phone: item.phone, services: item.services ?? [], visible: item.visible };
      const { data } = item.id ? await api.put(`/admin/shops/${item.id}`, body) : await api.post("/admin/shops", body);
      setList((p) => (item.id ? p.map((x) => (x.id === data.id ? data : x)) : [data, ...p]));
      setEdit(null);
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this shop?")) return;
    await api.delete(`/admin/shops/${id}`).catch(() => {});
    setList((p) => p.filter((x) => x.id !== id));
  };

  const shown = list.filter((s) => !q || `${s.name} ${s.district} ${s.pincode} ${s.address}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-shops">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button className="btn-primary justify-center" onClick={() => setEdit({ ...EMPTY })} data-testid="shop-add-btn"><Plus size={16} /> Add partner shop</button>
        <ShopImport onDone={load} />
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, district, pincode…" className="h-10 w-full rounded-lg border border-white/10 bg-card pl-9 pr-3 text-sm outline-none focus:border-saffron" data-testid="shops-search-input" />
        </div>
        <p className="text-sm text-slate-400" data-testid="shops-count">{list.filter((s) => s.visible).length} live · {list.length} total</p>
      </div>
      <p className="mt-3 text-xs text-slate-400">Live shops power the map counts and the “Find a shop near you” search. Shop name, address, pincode and phone are shown publicly.</p>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-card text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["Shop", "District", "Pincode", "Phone", "Services", "Live", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {shown.map((s) => (
              <tr key={s.id} data-testid={`shop-row-${s.id}`}>
                <td className="px-4 py-3"><p className="font-medium text-white">{s.name}</p><p className="text-xs text-slate-400">{s.address}</p></td>
                <td className="px-4 py-3">{s.district}</td>
                <td className="px-4 py-3 font-mono">{s.pincode}</td>
                <td className="px-4 py-3 font-mono text-slate-300">{s.phone || "—"}</td>
                <td className="px-4 py-3"><div className="flex max-w-[220px] flex-wrap gap-1">{(s.services ?? []).map((k) => <span key={k} className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] uppercase text-slate-300">{k}</span>)}</div></td>
                <td className="px-4 py-3"><Switch checked={s.visible} onCheckedChange={(v) => save({ ...s, visible: v })} data-testid={`shop-visible-${s.id}`} /></td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEdit(s)} className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white" aria-label="Edit" data-testid={`shop-edit-${s.id}`}><Pencil size={16} /></button>
                    <button onClick={() => remove(s.id)} className="rounded-lg p-2 text-slate-400 hover:bg-red-500/10 hover:text-red-300" aria-label="Delete" data-testid={`shop-delete-${s.id}`}><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={7} className="px-4 py-14 text-center text-slate-400" data-testid="shops-empty">No partner shops yet</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-card text-white">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "New"} partner shop</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={(e) => { e.preventDefault(); save(edit); }} className="space-y-4" data-testid="shop-form">
              <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400">Shop name</span>
                <input required minLength={2} className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="shop-input-name" />
              </label>
              <div>
                <span className="text-xs uppercase tracking-wider text-slate-400">District</span>
                <Select value={edit.district} onValueChange={(v) => setEdit({ ...edit, district: v })}>
                  <SelectTrigger className="mt-1 bg-ink" data-testid="shop-input-district"><SelectValue placeholder="Choose district" /></SelectTrigger>
                  <SelectContent>{map.districts.map((d) => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400">Address</span>
                <input required minLength={3} className={inputCls} value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} data-testid="shop-input-address" />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400">Pincode</span>
                  <input required inputMode="numeric" maxLength={6} pattern="[0-9]{6}" className={inputCls} value={edit.pincode} onChange={(e) => setEdit({ ...edit, pincode: e.target.value.replace(/\D/g, "") })} data-testid="shop-input-pincode" />
                </label>
                <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400">Phone (public)</span>
                  <input className={inputCls} value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} data-testid="shop-input-phone" />
                </label>
              </div>
              <div>
                <span className="text-xs uppercase tracking-wider text-slate-400">Services offered</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(SHOP_SERVICES).map(([k, v]) => {
                    const on = (edit.services ?? []).includes(k);
                    return (
                      <button type="button" key={k} onClick={() => setEdit({ ...edit, services: on ? edit.services.filter((x) => x !== k) : [...(edit.services ?? []), k] })}
                        className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-300 ${on ? "border-saffron bg-saffron text-ink" : "border-white/15 text-slate-300 hover:border-white/40"}`} data-testid={`shop-service-${k}`}>
                        {v.en}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button type="submit" disabled={!edit.district} className="btn-primary w-full justify-center" data-testid="shop-save-btn">Save shop</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
